"use client";

import * as React from "react";

import {
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuSearchSection,
  PopoverMenuScrollSection,
  PopoverMenuSection,
} from "@/components/ui/popover-menu";
import type { AgentSummary } from "@/lib/agents/types";
import { cn } from "@/lib/utils";


/**
 * Controlled agent picker for the per-task surfaces (new-task composer,
 * new-task modal, task detail). Mirrors the API of {@link TaskAccountMenu}/
 * {@link TaskAssigneeMenu}/{@link TaskStatusMenu}: caller hands in a
 * `trigger` element + a controlled `value`, picker owns its open state.
 *
 * Per-task agent assignment picker. This one just changes a single
 * task's agent assignment.
 */
type Props = {
  value: string | null;
  onChange: (agentId: string | null) => void;
  agents: AgentSummary[];
  trigger: React.ReactElement;
  disabled?: boolean;
};

export function TaskAgentMenu({
  value,
  onChange,
  agents,
  trigger,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const filteredAgents = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((wf) => wf.name.toLowerCase().includes(q));
  }, [agents, query]);

  const showEmptyState = agents.length === 0 && !query.trim();
  const hasListBelowNoAgent = filteredAgents.length > 0 || showEmptyState;

  const select = (agentId: string | null) => {
    onChange(agentId);
    setOpen(false);
    setQuery("");
  };

  return (
    <PopoverMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      trigger={trigger}
      menuContentClassName="w-[15.125rem] p-0"
      menu={
        <>
          <PopoverMenuSearchSection
            fullWidthDividerAfter
            inputProps={{
              value: query,
              onChange: (event) => setQuery(event.target.value),
              placeholder: "Search...",
            }}
          />

          <PopoverMenuSection
            fullWidthDividerAfter={hasListBelowNoAgent}
            className={cn("px-1", !hasListBelowNoAgent && "pb-1.5")}
          >
            <PopoverMenuItem
              label="No agent"
              selected={!value}
              disabled={disabled}
              onClick={() => select(null)}
            />
          </PopoverMenuSection>

          {filteredAgents.length > 0 ? (
            <PopoverMenuScrollSection
              className="px-1 pb-1.5"
              maxHeightClassName="max-h-[12rem]"
            >
              {filteredAgents.map((wf) => (
                <PopoverMenuItem
                  key={wf.id}
                  label={wf.name}
                  selected={wf.id === value}
                  disabled={disabled}
                  onClick={() => select(wf.id)}
                />
              ))}
            </PopoverMenuScrollSection>
          ) : showEmptyState ? (
            <PopoverMenuSection className="px-1 pb-1.5">
              <p className="px-2 py-1.5 text-[12px] text-neutral-07">
                No agents yet.
              </p>
            </PopoverMenuSection>
          ) : null}
        </>
      }
    />
  );
}
